/**
 * NewVersionBanner (power#1224)
 *
 * Non-dismissible app-shell banner shown when a newer frontend build has been
 * deployed. Clicking Reload performs a CLEAN reload: it first runs the app's
 * existing unsaved-changes checks, then releases every edit lock this tab
 * holds, and only then reloads the document.
 *
 * Why the click matters: releasing locks needs an awaitable window, and a
 * reload triggered from an effect or from `beforeunload` does not have one — the
 * existing unload handlers can only fire a best-effort `keepalive` DELETE. A
 * user-initiated click is an ordinary async handler, so we can release, confirm,
 * and then reload.
 *
 * Non-dismissible by decision: there is no X and no snooze. It is also strictly
 * NON-BLOCKING — no modal, no route blocking — so the user can finish and save
 * before clicking.
 */

import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { RefreshCw, Loader2, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { logger } from "@/services/logger";
import { useNavigation } from "@/contexts/NavigationContext";
import { useAppVersionStore } from "@/stores/useAppVersionStore";
import { useHeldLocksStore } from "@/stores/useHeldLocksStore";
import { markReloadedFor } from "@/utils/appVersion";

/**
 * Hard ceiling on the lock-release step. A hung lock API must never trap the
 * user on a stale bundle: past this we reload anyway. The `pagehide` keepalive
 * handlers still fire as a second chance, and the server-side lock TTL is the
 * final backstop.
 */
const LOCK_RELEASE_TIMEOUT_MS = 2000;

export function NewVersionBanner() {
  const { t } = useTranslation();
  const location = useLocation();
  const pendingVersion = useAppVersionStore((state) => state.pendingVersion);
  const isReloading = useAppVersionStore((state) => state.isReloading);
  const setReloading = useAppVersionStore((state) => state.setReloading);
  const { checkWorkflowNavigation, checkTimelineNavigation } = useNavigation();

  // Synchronous re-entry guard. `isReloading` is store state and the button's
  // `disabled` only applies after a re-render, so two clicks in the same tick
  // could otherwise both get through.
  const startedRef = useRef(false);

  const handleReload = useCallback(async () => {
    if (!pendingVersion || isReloading || startedRef.current) return;
    startedRef.current = true;
    setReloading(true);

    // 1. Unsaved work. Reuse the SAME pre-nav checks the app uses when the user
    //    navigates away, so they get the familiar Save / Discard / Cancel
    //    dialog rather than a bespoke one. Cancelling aborts the reload and
    //    leaves the banner (and the lock) in place.
    //    Target path is the current one: a reload lands where we already are.
    //
    //    This has its OWN error handling, deliberately NOT shared with the lock
    //    release below: if we cannot establish that it is safe to discard the
    //    document, we must not reload. Reloading past a failed check would
    //    throw away unsaved work.
    try {
      const proceed =
        (await checkWorkflowNavigation(location.pathname)) &&
        (await checkTimelineNavigation(location.pathname));

      if (!proceed) {
        logger.info("New-version reload cancelled by unsaved-changes check", {
          pendingVersion,
        });
        startedRef.current = false;
        setReloading(false);
        return;
      }
    } catch (error) {
      logger.error(
        "Unsaved-changes check failed before new-version reload — not reloading",
        error instanceof Error ? error : new Error("Unknown error"),
        { pendingVersion },
      );
      startedRef.current = false;
      setReloading(false);
      return;
    }

    // 2. Release every lock this tab still holds. The pre-nav checks above may
    //    already have released some (Save/Discard do); the registry is kept in
    //    sync by the lock hooks, so what remains here is what is genuinely
    //    still held. Each entry releases via its OWN hook path, so
    //    sessionStorage lockId keys are cleared correctly.
    //
    //    Failure here does NOT abort: a hung or broken lock API must never
    //    strand the user on a stale bundle. The `pagehide` keepalive handlers
    //    still fire, and the server-side lock TTL is the final backstop.
    try {
      const held = useHeldLocksStore.getState().snapshot();
      if (held.length > 0) {
        logger.info("Releasing held locks before new-version reload", {
          count: held.length,
          locks: held.map((l) => `${l.kind}:${l.resourceId}`).join(","),
        });
        await Promise.race([
          Promise.allSettled(held.map((l) => l.release())),
          new Promise((resolve) =>
            setTimeout(resolve, LOCK_RELEASE_TIMEOUT_MS),
          ),
        ]);
      }
    } catch (error) {
      logger.error(
        "Lock release before new-version reload failed — reloading anyway",
        error instanceof Error ? error : new Error("Unknown error"),
        { pendingVersion },
      );
    }

    // 3. Record the attempt (loop guard), then reload.
    markReloadedFor(pendingVersion);
    window.location.reload();
  }, [
    pendingVersion,
    isReloading,
    setReloading,
    checkWorkflowNavigation,
    checkTimelineNavigation,
    location.pathname,
  ]);

  const clearPending = useAppVersionStore((state) => state.clearPending);

  const handleDismiss = useCallback(() => {
    clearPending();
  }, [clearPending]);

  if (!pendingVersion) return null;

  return (
    <Alert
      variant="info"
      className="rounded-none border-x-0 border-t-0 border-b border-b-[#2a4d6e] bg-[#173049] flex items-center justify-between gap-4 py-3"
      data-testid="new-version-banner"
      role="status"
    >
      <div className="flex items-center gap-2 text-[#6ab6f5]">
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        <AlertDescription className="text-[#6ab6f5]">
          {t("newVersion.message")}
        </AlertDescription>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleReload}
          disabled={isReloading}
          data-testid="new-version-reload-button"
          className="bg-[#0066CC] hover:bg-[#0052A3] text-white font-semibold"
        >
          {isReloading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t("newVersion.reloading")}
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {t("newVersion.reload")}
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={handleDismiss}
          aria-label="Dismiss"
          data-testid="new-version-dismiss-button"
          className="h-8 w-8 p-0 text-[#6ab6f5] hover:bg-[#0066CC]/20 hover:text-[#6ab6f5]"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </Alert>
  );
}
